/**
 * AcademicTransfer E-Learning Course Data
 * Microlearning Roadmap Journey - 3-5 minute lessons
 * Based on PDF proposal structure: 3 Modules, 11 Tools, 24 Micro-lessons (steps)
 *
 * Lesson Types:
 * - learn: 📖 Educational content, reading
 * - action: 🎯 External tool usage, hands-on
 * - assess: ✅ Self-assessment, quizzes
 * - explore: 🔍 Resource discovery, browsing
 * - summary: 📋 Review, completion
 */

const COURSE_DATA = {
  version: '4.0',
  totalTools: 11,

  // Journey metadata
  journey: {
    titleKey: 'journey.title',
    subtitleKey: 'journey.subtitle',
    welcomeKey: 'journey.welcome',
    estimatedTotalTime: 145, // minutes — sum of per-step estimatedTime across all modules (M1 ~20 + M2 ~75 + M3 ~50)
    milestones: [
      {
        id: 'discover',
        moduleId: 'module-1',
        icon: 'compass',
        labelKey: 'journey.milestones.discover'
      },
      {
        id: 'reflect',
        moduleId: 'module-2',
        icon: 'circle-user',
        labelKey: 'journey.milestones.reflect'
      },
      {
        id: 'connect',
        moduleId: 'module-3',
        icon: 'target',
        labelKey: 'journey.milestones.connect'
      }
    ]
  },

  modules: [
    // =====================================================
    // MODULE 1: Getting to Know AcademicTransfer (6 steps)
    // Learning objectives: Understand AT platform, explore Career Navigator, create account
    // Restructured: Career Navigator BEFORE account creation (engagement-first approach)
    // =====================================================
    {
      id: 'module-1',
      titleKey: 'modules.m1.title',
      descriptionKey: 'modules.m1.description',
      icon: 'compass',
      color: '#5b9bd5',
      journeyLabel: 'Discover',
      steps: [
        // --- STEP 1.0: Module Intro (LEARNING OBJECTIVES) ---
        // Short on-boarding screen. "By the end of this module, you will have..."
        {
          id: '1.0',
          titleKey: 'modules.m1.intro.title',
          descriptionKey: 'modules.m1.intro.subtitle',
          actionType: 'module-intro',
          lessonType: 'learn',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 2,
          objectiveKeys: [
            'modules.m1.intro.objective1',
            'modules.m1.intro.objective2',
            'modules.m1.intro.objective3',
            'modules.m1.intro.objective4'
          ]
        },

        // --- STEP 1.1: Getting ready: a quick check (ENERGIZE) ---
        // Warm-up MCQ questions to get thoughts flowing
        {
          id: '1.1',
          titleKey: 'steps.s1_1.title',
          descriptionKey: 'steps.s1_1.description',
          image: 'step-1-1.svg',
          actionType: 'assess',
          lessonType: 'assess',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 3,
          tipKey: 'steps.s1_1.tip',
          transitionKey: 'steps.s1_1.transition',

          // Assessment: 3 MCQ questions (skills, career paths, career stage)
          assessment: {
            type: 'energize-check',
            titleKey: 'steps.s1_1.assessmentTitle',
            introKey: 'steps.s1_1.assessmentIntro',
            questions: [
              // Q1: Career Paths (moved up)
              {
                id: 'careerPaths',
                questionKey: 'steps.s1_1.q2.question',
                type: 'single-choice',
                options: [
                  { id: 'a', labelKey: 'steps.s1_1.q2.optionA' },
                  { id: 'b', labelKey: 'steps.s1_1.q2.optionB' },
                  { id: 'c', labelKey: 'steps.s1_1.q2.optionC' },
                  { id: 'd', labelKey: 'steps.s1_1.q2.optionD' }
                ]
              },
              // Q2: Skills / Strengths (moved down)
              {
                id: 'skills',
                questionKey: 'steps.s1_1.q1.question',
                type: 'single-choice',
                options: [
                  { id: 'a', labelKey: 'steps.s1_1.q1.optionA' },
                  { id: 'b', labelKey: 'steps.s1_1.q1.optionB' },
                  { id: 'c', labelKey: 'steps.s1_1.q1.optionC' },
                  { id: 'd', labelKey: 'steps.s1_1.q1.optionD' }
                ]
              },
              // Q3: Career Stage (kept from existing)
              {
                id: 'q1',
                questionKey: 'assessments.intro.q1.question',
                type: 'single-choice',
                options: [
                  { id: 'a', labelKey: 'assessments.intro.q1.optionA' },
                  { id: 'b', labelKey: 'assessments.intro.q1.optionB' },
                  { id: 'c', labelKey: 'assessments.intro.q1.optionC' },
                  { id: 'd', labelKey: 'assessments.intro.q1.optionD' }
                ]
              }
            ]
          }
        },

        // --- STEP 1.2: Why exploring career paths matters now (URGENCE) ---
        // Six career path cards for initial exploration
        {
          id: '1.2',
          titleKey: 'steps.s1_2.title',
          descriptionKey: 'steps.s1_2.description',
          image: 'step-1-2.svg',
          actionType: 'career-sectors',
          lessonType: 'action',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 3,
          tipKey: 'steps.s1_2.tip',
          transitionKey: 'steps.s1_2.transition',

          // Career paths content for selection
          content: {
            introKey: 'steps.s1_2.intro',
            paths: [
              {
                id: 'academicResearch',
                icon: 'graduation-cap',
                titleKey: 'careers.academicResearch.title',
                descKey: 'careers.academicResearch.desc',
                detailKey: 'careers.academicResearch.detail',
                traits: [
                  'careers.academicResearch.trait1',
                  'careers.academicResearch.trait2',
                  'careers.academicResearch.trait3'
                ]
              },
              {
                id: 'appliedSciences',
                icon: 'book-open',
                titleKey: 'careers.appliedSciences.title',
                descKey: 'careers.appliedSciences.desc',
                detailKey: 'careers.appliedSciences.detail',
                traits: [
                  'careers.appliedSciences.trait1',
                  'careers.appliedSciences.trait2',
                  'careers.appliedSciences.trait3'
                ]
              },
              {
                id: 'industryForProfit',
                icon: 'trending-up',
                titleKey: 'careers.industryForProfit.title',
                descKey: 'careers.industryForProfit.desc',
                detailKey: 'careers.industryForProfit.detail',
                traits: [
                  'careers.industryForProfit.trait1',
                  'careers.industryForProfit.trait2',
                  'careers.industryForProfit.trait3'
                ]
              },
              {
                id: 'entrepreneurship',
                icon: 'rocket',
                titleKey: 'careers.entrepreneurship.title',
                descKey: 'careers.entrepreneurship.desc',
                detailKey: 'careers.entrepreneurship.detail',
                traits: [
                  'careers.entrepreneurship.trait1',
                  'careers.entrepreneurship.trait2',
                  'careers.entrepreneurship.trait3'
                ]
              },
              {
                id: 'notForProfit',
                icon: 'heart-handshake',
                titleKey: 'careers.notForProfit.title',
                descKey: 'careers.notForProfit.desc',
                detailKey: 'careers.notForProfit.detail',
                traits: [
                  'careers.notForProfit.trait1',
                  'careers.notForProfit.trait2',
                  'careers.notForProfit.trait3'
                ]
              },
              {
                id: 'government',
                icon: 'landmark',
                titleKey: 'careers.government.title',
                descKey: 'careers.government.desc',
                detailKey: 'careers.government.detail',
                traits: [
                  'careers.government.trait1',
                  'careers.government.trait2',
                  'careers.government.trait3'
                ]
              }
            ]
          }
        },

        // --- STEP 1.3: Career Navigator — Explore in depth (THEORY) ---
        // Tool launch with Career Navigator + reflection questions
        {
          id: '1.3',
          titleKey: 'steps.s1_3.title',
          descriptionKey: 'steps.s1_3.description',
          image: 'step-1-3.svg',
          actionType: 'career-exploration',
          lessonType: 'action',
          toolId: 'careerNavigator',
          toolUrlKey: 'careerNavigator',
          estimatedTime: 5,
          tipKey: 'steps.s1_3.tip',
          transitionKey: 'steps.s1_3.transition',

          // Tool launch content
          toolLaunch: {
            titleKey: 'steps.s1_3.toolTitle',
            descriptionKey: 'steps.s1_3.toolDescription',
            ctaKey: 'steps.s1_3.toolCta'
          },

          // Reflection questions after exploration
          assessment: {
            type: 'career-reflection',
            titleKey: 'steps.s1_3.reflection.title',
            introKey: 'steps.s1_3.reflection.intro',
            questions: [
              {
                id: 'interesting',
                questionKey: 'steps.s1_3.reflection.q1',
                type: 'text',
                placeholderKey: 'steps.s1_3.reflection.q1Placeholder'
              },
              {
                id: 'surprise',
                questionKey: 'steps.s1_3.reflection.q2',
                type: 'text',
                placeholderKey: 'steps.s1_3.reflection.q2Placeholder'
              },
              {
                id: 'notes',
                questionKey: 'steps.s1_3.reflection.notesLabel',
                type: 'text',
                placeholderKey: 'steps.s1_3.reflection.notesPlaceholder',
                optional: true
              }
            ]
          }
        },

        // --- STEP 1.4: Get full access to AcademicTransfer (PRACTICE) ---
        // Account creation CTA — the only required action in Module 1
        {
          id: '1.4',
          titleKey: 'steps.s1_4.title',
          descriptionKey: 'steps.s1_4.description',
          image: 'step-1-4.svg',
          actionType: 'external-link',
          lessonType: 'action',
          toolId: 'createAccount',
          toolUrlKey: 'createAccount',
          estimatedTime: 3,
          tipKey: 'steps.s1_4.tip',
          ctaTextKey: 'steps.s1_4.ctaButton',
          transitionKey: 'steps.s1_4.transition',

          // Intro content for the external-link step
          introContent: {
            titleKey: 'steps.s1_4.intro.title',
            descriptionKey: 'steps.s1_4.intro.description'
          }
        },

        // --- STEP 1.5: Step back and notice (REFLECTION) ---
        // Light exercise + optional notes + next module button
        {
          id: '1.5',
          titleKey: 'steps.s1_5.title',
          descriptionKey: 'steps.s1_5.description',
          image: 'step-1-5.svg',
          actionType: 'reflection',
          lessonType: 'assess',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 3,
          tipKey: 'steps.s1_5.tip',
          nextModuleButton: true,
          nextModuleButtonKey: 'steps.s1_5.nextModuleBtn',
          nextModuleIntroKey: 'steps.s1_5.nextModuleIntro',

          // Light exercise summary
          summary: {
            titleKey: 'steps.s1_5.exercise.title',
            contentKey: 'steps.s1_5.exercise.content',
            keyPoints: ['steps.s1_5.exercise.point1', 'steps.s1_5.exercise.point2'],
            icon: 'leaf'
          },

          // Optional notes textarea
          assessment: {
            type: 'reflection-notes',
            introKey: 'steps.s1_5.notesIntro',
            questions: [
              {
                id: 'reflectionNotes',
                questionKey: 'steps.s1_5.notesLabel',
                type: 'text',
                placeholderKey: 'steps.s1_5.notesPlaceholder',
                optional: true
              }
            ]
          }
        }
      ]
    },

    // =====================================================
    // MODULE 2: Mapping Yourself (6 micro-lessons)
    // Learning objectives: Assess competencies, reflect on career, build personal pitch
    // =====================================================
    {
      id: 'module-2',
      titleKey: 'modules.m2.title',
      descriptionKey: 'modules.m2.description',
      icon: 'circle-user',
      color: '#4aaa6a',
      journeyLabel: 'Reflect',
      steps: [
        // --- STEP 2.0: Module Intro (LEARNING OBJECTIVES) ---
        // Short on-boarding screen. "By the end of this module, you will have..."
        {
          id: '2.0',
          titleKey: 'modules.m2.intro.title',
          descriptionKey: 'modules.m2.intro.subtitle',
          actionType: 'module-intro',
          lessonType: 'learn',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 2,
          objectiveKeys: [
            'modules.m2.intro.objective1',
            'modules.m2.intro.objective2',
            'modules.m2.intro.objective3',
            'modules.m2.intro.objective4'
          ]
        },

        // --- MODULE 2 INTRO: Transferable Skills Introduction ---
        {
          id: '2.1',
          titleKey: 'steps.s2_0.title',
          descriptionKey: 'steps.s2_0.description',
          image: 'step-2-0-v2.svg',
          actionType: 'learn-assess',
          lessonType: 'learn',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 3,
          tipKey: 'steps.s2_0.tip',
          transitionKey: 'steps.s2_0.transition',

          summary: {
            icon: 'target'
          },

          // Assessment: 3 MCQ warm-up questions (like module 1)
          assessment: {
            type: 'energize-check',
            titleKey: 'steps.s2_0.assessmentTitle',
            introKey: 'steps.s2_0.assessmentIntro',
            questions: [
              {
                id: 'transferableSkills',
                questionKey: 'steps.s2_0.q1.question',
                type: 'single-choice',
                options: [
                  { id: 'a', labelKey: 'steps.s2_0.q1.optionA' },
                  { id: 'b', labelKey: 'steps.s2_0.q1.optionB' },
                  { id: 'c', labelKey: 'steps.s2_0.q1.optionC' }
                ]
              },
              {
                id: 'assessmentExpectation',
                questionKey: 'steps.s2_0.q2.question',
                type: 'single-choice',
                options: [
                  { id: 'a', labelKey: 'steps.s2_0.q2.optionA' },
                  { id: 'b', labelKey: 'steps.s2_0.q2.optionB' },
                  { id: 'c', labelKey: 'steps.s2_0.q2.optionC' }
                ]
              },
              {
                id: 'reflectionApproach',
                questionKey: 'steps.s2_0.q3.question',
                type: 'single-choice',
                options: [
                  { id: 'a', labelKey: 'steps.s2_0.q3.optionA' },
                  { id: 'b', labelKey: 'steps.s2_0.q3.optionB' },
                  { id: 'c', labelKey: 'steps.s2_0.q3.optionC' }
                ]
              }
            ]
          },

          // Test choice section — order matches the order in which the tools
          // appear in the e-learning (PhD Competence Model first, then Career Reflector).
          testChoice: {
            titleKey: 'steps.s2_0.testChoiceTitle',
            introKey: 'steps.s2_0.testChoiceIntro',
            options: [
              {
                id: 'phd-competence',
                titleKey: 'steps.s2_0.phdCompetence.title',
                descriptionKey: 'steps.s2_0.phdCompetence.description',
                features: ['steps.s2_0.phdCompetence.feature1'],
                icon: 'clipboard-check',
                duration: 10,
                stepId: '2.3'
              },
              {
                id: 'career-reflector',
                titleKey: 'steps.s2_0.careerReflector.title',
                descriptionKey: 'steps.s2_0.careerReflector.description',
                features: ['steps.s2_0.careerReflector.feature1'],
                icon: 'brain',
                duration: 40,
                toolUrlKey: 'careerReflector'
              }
            ]
          }
        },

        // --- MICRO-LESSON 2.1: Competency Domain Overview ---
        {
          id: '2.2',
          titleKey: 'steps.s2_1_intro.title',
          descriptionKey: 'steps.s2_1_intro.description',
          image: 'step-2-1.svg',
          actionType: 'drag-drop-exercise',
          lessonType: 'learn',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 2,
          tipKey: 'steps.s2_1_intro.tip',
          transitionKey: 'steps.s2_1.transition'
        },

        // --- MICRO-LESSON 2.2: Complete PhD Competence Model (ACTION) ---
        {
          id: '2.3',
          titleKey: 'steps.s2_1.title',
          descriptionKey: 'steps.s2_1.description',
          image: 'step-2-2.svg',
          actionType: 'competence-assessment',
          lessonType: 'action',
          toolId: 'phdCompetence',
          toolUrlKey: 'phdCompetence',
          confirmationType: 'checkbox',
          confirmationKey: 'steps.s2_1.confirmation',
          estimatedTime: 10,
          tipKey: 'steps.s2_1.tip',
          transitionKey: 'steps.s2_2.transition',

          summary: {
            titleKey: 'summaries.competence.title',
            icon: 'star'
          },

          assessment: {
            type: 'competence-radar',
            titleKey: 'assessments.competence.title',
            introKey: 'assessments.competence.intro',
            instructionKey: 'assessments.competence.instruction',
            domains: [
              { id: 'research', labelKey: 'competencies.research' },
              { id: 'personal', labelKey: 'competencies.personal' },
              { id: 'professional', labelKey: 'competencies.professional' },
              { id: 'leadership', labelKey: 'competencies.leadership' },
              { id: 'communication', labelKey: 'competencies.communication' },
              { id: 'teaching', labelKey: 'competencies.teaching' }
            ],
            scaleDescription: {
              1: 'assessments.competence.scale1',
              2: 'assessments.competence.scale2',
              3: 'assessments.competence.scale3',
              4: 'assessments.competence.scale4',
              5: 'assessments.competence.scale5'
            },
            feedbackKey: 'assessments.competence.feedback'
          }
        },

        // --- MICRO-LESSON 2.3: Competence Action Plan (ASSESS) ---
        {
          id: '2.4',
          titleKey: 'steps.s2_1_reflect.title',
          descriptionKey: 'steps.s2_1_reflect.description',
          image: 'step-2-3.svg',
          actionType: 'reflection',
          lessonType: 'assess',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 4,
          tipKey: 'steps.s2_1_reflect.tip',

          assessment: {
            type: 'reflection-questions',
            titleKey: 'steps.s2_1_reflect.title',
            introTitleKey: 'steps.s2_1_reflect.assessmentTitle',
            introKey: 'steps.s2_1_reflect.assessmentIntro',
            questions: [
              {
                id: 'competence-selection',
                questionKey: 'steps.s2_1_reflect.q1.question',
                type: 'text',
                placeholderKey: 'steps.s2_1_reflect.q1.placeholder'
              },
              {
                id: 'action-plan',
                questionKey: 'steps.s2_1_reflect.q2.question',
                type: 'text',
                placeholderKey: 'steps.s2_1_reflect.q2.placeholder'
              },
              {
                id: 'progress-measurement',
                questionKey: 'steps.s2_1_reflect.q3.question',
                type: 'text',
                placeholderKey: 'steps.s2_1_reflect.q3.placeholder'
              }
            ],
            feedbackKey: 'steps.s2_1_reflect.assessmentFeedback'
          }
        },

        // --- MICRO-LESSON 2.5: Career Reflector — intro, launch & working-style reflection (ACTION) ---
        {
          id: '2.5',
          titleKey: 'steps.s2_2_intro.title',
          descriptionKey: 'steps.s2_2_intro.description',
          image: 'step-2-4.svg',
          actionType: 'external-link',
          lessonType: 'action',
          toolId: 'careerReflector',
          toolUrlKey: 'careerReflector',
          estimatedTime: 40,
          tipKey: 'steps.s2_2_intro.tip',
          transitionKey: 'steps.s2_5.transition',

          // "What This Assessment Measures" list (item1..4) shown above the tool CTA.
          introContent: {
            benefitsKey: 'steps.s2_2_intro.whatYouDiscover'
          },
          ctaTextKey: 'steps.s2_2_intro.cta',

          // Single working-style reflection kept from the old 2.6 (q2 — the
          // collaboration/leadership strength that feeds the 2.7 pitch). q1/q3
          // were dropped: the new 2.6 insight->criteria exercise covers that ground.
          reflection: {
            titleKey: 'steps.s2_2.title',
            introKey: 'steps.s2_2.assessmentIntro',
            questions: [
              {
                id: 'collaboration-strength',
                questionKey: 'steps.s2_2.q2.question',
                placeholderKey: 'steps.s2_2.q2.placeholder'
              }
            ]
          }
        },

        // --- MICRO-LESSON 2.6: From Insights to Workplace Criteria (ASSESS) ---
        {
          id: '2.6',
          titleKey: 'steps.s2_2_criteria.title',
          descriptionKey: 'steps.s2_2_criteria.description',
          image: 'step-2-5.svg',
          actionType: 'insight-criteria',
          lessonType: 'assess',
          estimatedTime: 10,
          instructionKey: 'steps.s2_2_criteria.instruction',
          columnInsightKey: 'steps.s2_2_criteria.columnInsight',
          columnCriteriaKey: 'steps.s2_2_criteria.columnCriteria',
          exampleLabelKey: 'steps.s2_2_criteria.exampleLabel',
          examples: [
            {
              insightKey: 'steps.s2_2_criteria.example1Insight',
              criteriaKey: 'steps.s2_2_criteria.example1Criteria'
            },
            {
              insightKey: 'steps.s2_2_criteria.example2Insight',
              criteriaKey: 'steps.s2_2_criteria.example2Criteria'
            }
          ],
          placeholderInsightKey: 'steps.s2_2_criteria.placeholderInsight',
          placeholderCriteriaKey: 'steps.s2_2_criteria.placeholderCriteria',
          addRowKey: 'steps.s2_2_criteria.addRow',
          tipKey: 'steps.s2_2_criteria.tip',
          transitionKey: 'steps.s2_2_criteria.transition'
        },

        // --- MICRO-LESSON 2.6: Build Your Personal Pitch (ACTION + ASSESS) ---
        {
          id: '2.7',
          titleKey: 'steps.s2_3.title',
          descriptionKey: 'steps.s2_3.description',
          image: 'step-2-6.svg',
          actionType: 'pitch-builder',
          lessonType: 'action',
          toolId: 'personalPitch',
          toolUrlKey: 'personalPitch',
          confirmationType: 'checkbox',
          confirmationKey: 'steps.s2_3.confirmation',
          estimatedTime: 5,
          tipKey: 'steps.s2_3.tip',
          transitionKey: 'steps.s2_3.transition',

          summary: {
            titleKey: 'summaries.pitch.title',
            contentTitleKey: 'summaries.pitch.contentTitle',
            contentKey: 'summaries.pitch.content',
            keyPoints: [
              'summaries.pitch.point1',
              'summaries.pitch.point2',
              'summaries.pitch.point3',
              'summaries.pitch.point4'
            ],
            icon: 'mic'
          },

          // Personal Pitch Builder
          content: {
            type: 'pitchBuilder',
            template: {
              titleKey: 'pitch.template.title',
              sections: [
                {
                  id: 'intro',
                  labelKey: 'pitch.template.intro',
                  promptKey: 'pitch.template.intro_prompt'
                },
                {
                  id: 'expertise',
                  labelKey: 'pitch.template.expertise',
                  promptKey: 'pitch.template.expertise_prompt'
                },
                {
                  id: 'motivation',
                  labelKey: 'pitch.template.motivation',
                  promptKey: 'pitch.template.motivation_prompt'
                },
                {
                  id: 'unique',
                  labelKey: 'pitch.template.unique',
                  promptKey: 'pitch.template.unique_prompt'
                },
                {
                  id: 'goal',
                  labelKey: 'pitch.template.goal',
                  promptKey: 'pitch.template.goal_prompt'
                }
              ]
            }
          },

          assessment: {
            type: 'pitch-checklist',
            titleKey: 'assessments.pitch.title',
            introKey: 'assessments.pitch.intro',
            criteria: [
              { id: 'clear', labelKey: 'assessments.pitch.criteria1' },
              { id: 'concise', labelKey: 'assessments.pitch.criteria2' },
              { id: 'unique', labelKey: 'assessments.pitch.criteria3' },
              { id: 'memorable', labelKey: 'assessments.pitch.criteria4' },
              { id: 'authentic', labelKey: 'assessments.pitch.criteria5' }
            ],
            feedbackKey: 'assessments.pitch.feedback'
          }
        }
      ]
    },

    // =====================================================
    // MODULE 3: Making the Match (10 micro-lessons)
    // Learning objectives: CV matching, archive analysis, research matching,
    // networking, career inspiration, wrap-up.
    // =====================================================
    {
      id: 'module-3',
      titleKey: 'modules.m3.title',
      descriptionKey: 'modules.m3.description',
      icon: 'target',
      color: '#4a9f96',
      journeyLabel: 'Connect',
      steps: [
        // --- STEP 3.0: Module Intro (LEARNING OBJECTIVES) ---
        // Short on-boarding screen. "By the end of this module, you will have..."
        {
          id: '3.0',
          titleKey: 'modules.m3.intro.title',
          descriptionKey: 'modules.m3.intro.subtitle',
          actionType: 'module-intro',
          lessonType: 'learn',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 2,
          objectiveKeys: [
            'modules.m3.intro.objective1',
            'modules.m3.intro.objective2',
            'modules.m3.intro.objective3',
            'modules.m3.intro.objective4'
          ]
        },

        // --- MODULE 3 INTRO: Making the Match ---
        // Module 3 convention: only the title sits beside the drawing.
        // The explanation lives in summary.contentKey (rendered as step-about-text below).
        {
          id: '3.1',
          titleKey: 'steps.s3_0.title',
          image: 'step-3-0.svg',
          actionType: 'learn-assess',
          lessonType: 'learn',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 3,
          tipKey: 'steps.s3_0.tip',
          transitionKey: 'steps.s3_0.transition',

          summary: {
            titleKey: 'steps.s3_0.title',
            contentKey: 'steps.s3_0.content',
            icon: 'target'
          },

          // Tool overview section
          toolOverview: {
            titleKey: 'steps.s3_0.toolsTitle',
            tools: [
              { id: 'cvMatch', titleKey: 'steps.s3_0.tools.cvMatch', icon: 'file-text' },
              {
                id: 'researchMatch',
                titleKey: 'steps.s3_0.tools.researchMatch',
                icon: 'microscope'
              },
              { id: 'findSupervisor', titleKey: 'steps.s3_0.tools.findSupervisor', icon: 'users' },
              { id: 'buildNetwork', titleKey: 'steps.s3_0.tools.buildNetwork', icon: 'network' },
              { id: 'getInspired', titleKey: 'steps.s3_0.tools.getInspired', icon: 'lightbulb' }
            ]
          }
        },

        // --- MICRO-LESSON 3.2: Use CV Match Tool (ACTION) ---
        // Title-only beside drawing; the explanation lives in introContent
        // (rendered inside the tool-launch card by ToolLaunchStep).
        {
          id: '3.2',
          titleKey: 'steps.s3_1.title',
          image: 'step-3-2.svg',
          actionType: 'cv-analyzer',
          lessonType: 'action',
          toolId: 'cvMatch',
          toolUrlKey: 'cvMatch',
          ctaTextKey: 'steps.s3_1.ctaPrimary',
          confirmationType: 'checkbox',
          confirmationKey: 'steps.s3_1.confirmation',
          estimatedTime: 5,
          tipKey: 'steps.s3_1_intro.tip',
          transitionKey: 'steps.s3_1.transition',
          introContent: {
            descriptionKey: 'steps.s3_1.content'
          },

          guideTitleKey: 'steps.s3_1.guide.title',
          guideIntroKey: 'steps.s3_1.guide.intro',
          guideLayout: 'vertical',
          guideSteps: [
            {
              id: 'search',
              labelKey: 'steps.s3_1.guide.step1.label',
              descKey: 'steps.s3_1.guide.step1.desc'
            },
            {
              id: 'filter',
              labelKey: 'steps.s3_1.guide.step2.label',
              descKey: 'steps.s3_1.guide.step2.desc'
            },
            {
              id: 'upload',
              labelKey: 'steps.s3_1.guide.step3.label',
              descKey: 'steps.s3_1.guide.step3.desc'
            },
            {
              id: 'review',
              labelKey: 'steps.s3_1.guide.step4.label',
              descKey: 'steps.s3_1.guide.step4.desc'
            }
          ],

          summary: {
            titleKey: 'summaries.cvmatch.title',
            contentKey: 'summaries.cvmatch.content',
            icon: 'file-text'
          },

          assessment: {
            type: 'cv-checklist',
            titleKey: 'assessments.cv.title',
            introKey: 'assessments.cv.intro',
            sections: [
              {
                id: 'content',
                titleKey: 'assessments.cv.section1.title',
                items: [
                  { id: 'c1', labelKey: 'assessments.cv.section1.item1' },
                  { id: 'c2', labelKey: 'assessments.cv.section1.item2' },
                  { id: 'c3', labelKey: 'assessments.cv.section1.item3' },
                  { id: 'c4', labelKey: 'assessments.cv.section1.item4' }
                ]
              },
              {
                id: 'format',
                titleKey: 'assessments.cv.section2.title',
                items: [
                  { id: 'f1', labelKey: 'assessments.cv.section2.item1' },
                  { id: 'f2', labelKey: 'assessments.cv.section2.item2' },
                  { id: 'f3', labelKey: 'assessments.cv.section2.item3' }
                ]
              }
            ],
            feedbackKey: 'assessments.cv.feedback'
          }
        },

        // --- MICRO-LESSON 3.3a: Learning from Expired Vacancies (REFLECT) ---
        // Title-only beside drawing; explanation, 4-step archive guide,
        // and the reflection exercise are stacked in the body.
        // Uses ReflectionStep with an optional guideSteps field.
        {
          id: '3.3',
          titleKey: 'steps.s3_1_archive.title',
          image: 'step-3-1.svg',
          actionType: 'reflection',
          lessonType: 'assess',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 5,
          tipKey: 'steps.s3_1_archive.tip',
          transitionKey: 'steps.s3_1_archive.transition',

          summary: {
            contentKey: 'steps.s3_1_archive.content',
            icon: 'archive'
          },

          guideTitleKey: 'steps.s3_1_archive.guide.title',
          guideIntroKey: 'steps.s3_1_archive.guide.intro',
          guideLayout: 'vertical',
          guideSteps: [
            {
              id: 'visit',
              labelKey: 'steps.s3_1_archive.guide.step1.label',
              descKey: 'steps.s3_1_archive.guide.step1.desc'
            },
            {
              id: 'login',
              labelKey: 'steps.s3_1_archive.guide.step2.label',
              descKey: 'steps.s3_1_archive.guide.step2.desc'
            },
            {
              id: 'search',
              labelKey: 'steps.s3_1_archive.guide.step3.label',
              descKey: 'steps.s3_1_archive.guide.step3.desc'
            },
            {
              id: 'filter',
              labelKey: 'steps.s3_1_archive.guide.step4.label',
              descKey: 'steps.s3_1_archive.guide.step4.desc'
            }
          ],

          assessment: {
            type: 'archive-reflection',
            titleKey: 'assessments.archive.title',
            introKey: 'assessments.archive.intro',
            feedbackKey: 'assessments.archive.feedback',
            questions: [
              {
                id: 'archiveOrgs',
                titleKey: 'assessments.archive.q1.title',
                questionKey: 'assessments.archive.q1.question',
                type: 'text',
                placeholderKey: 'assessments.archive.q1.placeholder'
              },
              {
                id: 'archiveRoles',
                titleKey: 'assessments.archive.q2.title',
                questionKey: 'assessments.archive.q2.question',
                type: 'text',
                placeholderKey: 'assessments.archive.q2.placeholder'
              }
            ]
          }
        },

        // --- MICRO-LESSON 3.4: Research Match (ACTION) ---
        // Title-only beside drawing; explanation is rendered inside the
        // tool-launch card via introContent.descriptionKey.
        {
          id: '3.4',
          titleKey: 'steps.s3_2.title',
          image: 'step-3-3.svg',
          actionType: 'research-matcher',
          lessonType: 'action',
          toolId: 'researchMatch',
          toolUrlKey: 'researchMatch',
          ctaTextKey: 'steps.s3_2.ctaPrimary',
          confirmationType: 'checkbox',
          confirmationKey: 'steps.s3_2.confirmation',
          estimatedTime: 5,
          transitionKey: 'steps.s3_3.transition',
          introContent: {
            descriptionKey: 'steps.s3_2.content'
          },

          guideTitleKey: 'steps.s3_2.guide.title',
          guideIntroKey: 'steps.s3_2.guide.intro',
          guideLayout: 'vertical',
          guideSteps: [
            {
              id: 'search',
              labelKey: 'steps.s3_2.guide.step1.label',
              descKey: 'steps.s3_2.guide.step1.desc'
            },
            {
              id: 'filter',
              labelKey: 'steps.s3_2.guide.step2.label',
              descKey: 'steps.s3_2.guide.step2.desc'
            },
            {
              id: 'upload',
              labelKey: 'steps.s3_2.guide.step3.label',
              descKey: 'steps.s3_2.guide.step3.desc'
            },
            {
              id: 'review',
              labelKey: 'steps.s3_2.guide.step4.label',
              descKey: 'steps.s3_2.guide.step4.desc'
            }
          ],

          summary: {
            titleKey: 'summaries.research.title',
            contentKey: 'summaries.research.content',
            icon: 'search'
          },

          assessment: {
            type: 'research-reflection',
            titleKey: 'assessments.research.title',
            introKey: 'assessments.research.intro',
            questions: [
              {
                id: 'q1',
                questionKey: 'assessments.research.q1.question',
                type: 'text',
                placeholderKey: 'assessments.research.q1.placeholder'
              },
              {
                id: 'q2',
                questionKey: 'assessments.research.q2.question',
                type: 'multi-choice',
                options: [
                  { id: 'a', labelKey: 'assessments.research.q2.optionA' },
                  { id: 'b', labelKey: 'assessments.research.q2.optionB' },
                  { id: 'c', labelKey: 'assessments.research.q2.optionC' },
                  { id: 'd', labelKey: 'assessments.research.q2.optionD' }
                ],
                allowMultiple: true
              }
            ],
            feedbackKey: 'assessments.research.feedback'
          }
        },

        // --- MICRO-LESSON 3.5: Finding Supervisors (LEARN + ACTION) ---
        // Title-only beside drawing; explanation rendered inside the
        // tool-launch card via introContent.descriptionKey.
        {
          id: '3.5',
          titleKey: 'steps.s3_3.title',
          image: 'step-3-4.svg',
          actionType: 'external-link',
          lessonType: 'action',
          toolId: 'findSupervisor',
          toolUrlKey: 'findSupervisor',
          confirmationType: 'checkbox',
          confirmationKey: 'steps.s3_3.confirmation',
          estimatedTime: 5,
          tipKey: 'steps.s3_3.tip',
          transitionKey: 'steps.s3_4.transition',
          introContent: {
            descriptionKey: 'steps.s3_3.content'
          },

          guideTitleKey: 'steps.s3_3.guide.title',
          guideIntroKey: 'steps.s3_3.guide.intro',
          guideSteps: [
            {
              labelKey: 'steps.s3_3.guide.step1.label',
              descKey: 'steps.s3_3.guide.step1.desc'
            },
            {
              labelKey: 'steps.s3_3.guide.step2.label',
              descKey: 'steps.s3_3.guide.step2.desc'
            },
            {
              labelKey: 'steps.s3_3.guide.step3.label',
              descKey: 'steps.s3_3.guide.step3.desc'
            }
          ],

          summary: {
            titleKey: 'summaries.supervisor.title',
            contentKey: 'summaries.supervisor.content',
            keyPoints: [
              'summaries.supervisor.point1',
              'summaries.supervisor.point2',
              'summaries.supervisor.point3',
              'summaries.supervisor.point4'
            ],
            icon: 'users'
          },

          reflection: {
            titleKey: 'steps.s3_3.exercise.title',
            introKey: 'steps.s3_3.exercise.intro',
            questions: [
              {
                id: 'supervisorNames',
                questionKey: 'steps.s3_3.exercise.q1.question',
                placeholderKey: 'steps.s3_3.exercise.q1.placeholder'
              }
            ]
          }
        },

        // --- MICRO-LESSON 3.6: The Science of Networking (LEARN + ASSESS) ---
        // Title-only beside drawing; explanation in body, then a three-scenario
        // single-choice exercise with reveal-on-pick feedback callouts.
        // The 7-step framework that used to live here moved to step 3.8.
        {
          id: '3.6',
          titleKey: 'steps.s3_4_intro.title',
          image: 'step-3-5.svg',
          actionType: 'learn-assess',
          lessonType: 'assess',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 6,
          tipKey: 'steps.s3_4_intro.tip',
          transitionKey: 'steps.s3_4_intro.transition',

          summary: {
            contentKey: 'steps.s3_4_intro.content',
            icon: 'share-2'
          },

          assessment: {
            type: 'networking-dilemmas',
            titleKey: 'assessments.networkingDilemmas.title',
            introKey: 'assessments.networkingDilemmas.intro',
            questions: [
              {
                id: 'netDilemma1',
                type: 'single-choice',
                titleKey: 'assessments.networkingDilemmas.q1.title',
                questionKey: 'assessments.networkingDilemmas.q1.question',
                scenarioKey: 'assessments.networkingDilemmas.q1.scenario',
                feedbackKey: 'assessments.networkingDilemmas.q1.feedback',
                options: [
                  { id: 'a', labelKey: 'assessments.networkingDilemmas.q1.optionA' },
                  { id: 'b', labelKey: 'assessments.networkingDilemmas.q1.optionB' },
                  { id: 'c', labelKey: 'assessments.networkingDilemmas.q1.optionC', correct: true }
                ]
              },
              {
                id: 'netDilemma2',
                type: 'single-choice',
                titleKey: 'assessments.networkingDilemmas.q2.title',
                questionKey: 'assessments.networkingDilemmas.q2.question',
                scenarioKey: 'assessments.networkingDilemmas.q2.scenario',
                feedbackKey: 'assessments.networkingDilemmas.q2.feedback',
                options: [
                  { id: 'a', labelKey: 'assessments.networkingDilemmas.q2.optionA' },
                  { id: 'b', labelKey: 'assessments.networkingDilemmas.q2.optionB', correct: true },
                  { id: 'c', labelKey: 'assessments.networkingDilemmas.q2.optionC' }
                ]
              },
              {
                id: 'netDilemma3',
                type: 'single-choice',
                titleKey: 'assessments.networkingDilemmas.q3.title',
                questionKey: 'assessments.networkingDilemmas.q3.question',
                scenarioKey: 'assessments.networkingDilemmas.q3.scenario',
                feedbackKey: 'assessments.networkingDilemmas.q3.feedback',
                options: [
                  { id: 'a', labelKey: 'assessments.networkingDilemmas.q3.optionA', correct: true },
                  { id: 'b', labelKey: 'assessments.networkingDilemmas.q3.optionB' },
                  { id: 'c', labelKey: 'assessments.networkingDilemmas.q3.optionC' }
                ]
              }
            ]
          }
        },

        // --- MICRO-LESSON 3.7: Your 7-Step Networking Plan (LEARN + ASSESS) ---
        // Title-only beside drawing; explanation in body, the 7-step framework
        // rendered as a numbered guide grid (reused from step 3.4), and a
        // three-question free-text reflection. No external tool launch.
        {
          id: '3.7',
          titleKey: 'steps.s3_4.title',
          image: 'step-3-6.svg',
          actionType: 'reflection',
          lessonType: 'assess',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 8,
          tipKey: 'steps.s3_4.tip',
          transitionKey: 'steps.s3_6.transition',

          summary: {
            contentKey: 'steps.s3_4.content',
            icon: 'share-2'
          },

          guideTitleKey: 'steps.s3_4.guideTitle',
          guideLayout: 'vertical',
          guideSteps: [
            { id: 'goal', labelKey: 'networking.step1.label', descKey: 'networking.step1.desc' },
            { id: 'audit', labelKey: 'networking.step2.label', descKey: 'networking.step2.desc' },
            {
              id: 'identify',
              labelKey: 'networking.step3.label',
              descKey: 'networking.step3.desc'
            },
            {
              id: 'approach',
              labelKey: 'networking.step4.label',
              descKey: 'networking.step4.desc'
            },
            { id: 'plan', labelKey: 'networking.step5.label', descKey: 'networking.step5.desc' },
            { id: 'execute', labelKey: 'networking.step6.label', descKey: 'networking.step6.desc' },
            { id: 'maintain', labelKey: 'networking.step7.label', descKey: 'networking.step7.desc' }
          ],

          assessment: {
            type: 'network-plan-reflection',
            titleKey: 'assessments.networkPlan.title',
            introKey: 'assessments.networkPlan.intro',
            feedbackKey: 'assessments.networkPlan.feedback',
            questions: [
              {
                id: 'netPlanTarget',
                titleKey: 'assessments.networkPlan.q1.title',
                questionKey: 'assessments.networkPlan.q1.question',
                type: 'text',
                placeholderKey: 'assessments.networkPlan.q1.placeholder'
              },
              {
                id: 'netPlanWarmIntro',
                titleKey: 'assessments.networkPlan.q2.title',
                questionKey: 'assessments.networkPlan.q2.question',
                type: 'text',
                placeholderKey: 'assessments.networkPlan.q2.placeholder'
              },
              {
                id: 'netPlanFirstStep',
                titleKey: 'assessments.networkPlan.q3.title',
                questionKey: 'assessments.networkPlan.q3.question',
                type: 'text',
                placeholderKey: 'assessments.networkPlan.q3.placeholder'
              }
            ]
          }
        },

        // --- MICRO-LESSON 3.8: Career Inspiration Resources (EXPLORE) ---
        // Title-only beside drawing; explanation rendered inside the
        // tool-launch card via introContent.descriptionKey.
        {
          id: '3.8',
          titleKey: 'steps.s3_6.title',
          image: 'step-3-7.svg',
          actionType: 'resource-hub',
          lessonType: 'explore',
          toolId: 'careerInspiration',
          toolUrlKey: 'careerInspiration',
          confirmationType: 'checkbox',
          confirmationKey: 'steps.s3_6.confirmation',
          estimatedTime: 5,
          tipKey: 'steps.s3_6.tip',
          transitionKey: 'steps.s3_7.transition',
          introContent: {
            descriptionKey: 'steps.s3_6.content'
          },

          summary: {
            titleKey: 'summaries.inspiration.title',
            contentKey: 'summaries.inspiration.content',
            keyPoints: [
              'summaries.inspiration.point1',
              'summaries.inspiration.point2',
              'summaries.inspiration.point3',
              'summaries.inspiration.point4',
              'summaries.inspiration.point5'
            ],
            icon: 'lightbulb'
          },

          assessment: {
            type: 'goal-setting',
            titleKey: 'assessments.inspiration.title',
            introKey: 'assessments.inspiration.intro',
            questions: [
              {
                id: 'q1',
                questionKey: 'assessments.inspiration.q1.question',
                type: 'text',
                placeholderKey: 'assessments.inspiration.q1.placeholder'
              },
              {
                id: 'q2',
                questionKey: 'assessments.inspiration.q2.question',
                type: 'text',
                placeholderKey: 'assessments.inspiration.q2.placeholder'
              },
              {
                id: 'q3',
                questionKey: 'assessments.inspiration.q3.question',
                type: 'text',
                placeholderKey: 'assessments.inspiration.q3.placeholder'
              }
            ],
            feedbackKey: 'assessments.inspiration.feedback'
          }
        },

        // --- MICRO-LESSON 3.9: Wrap-up & Next Steps (SUMMARY) ---
        // Title-only beside drawing; toelichting + final reflection in body.
        // No transitionKey — this is the last step in the course.
        {
          id: '3.9',
          titleKey: 'steps.completion.title',
          image: 'step-3-8.svg',
          actionType: 'journey-summary',
          lessonType: 'summary',
          toolId: null,
          toolUrlKey: null,
          estimatedTime: 5,
          tipKey: 'steps.completion.tip',

          summary: {
            titleKey: 'summaries.completion.title',
            contentKey: 'summaries.completion.content',
            keyPoints: [
              'summaries.completion.point1',
              'summaries.completion.point2',
              'summaries.completion.point3'
            ],
            icon: 'trophy'
          },

          assessment: {
            type: 'final-reflection',
            titleKey: 'assessments.completion.title',
            introKey: 'assessments.completion.intro',
            questions: [
              {
                id: 'takeaway',
                questionKey: 'assessments.completion.q1.question',
                type: 'text',
                placeholderKey: 'assessments.completion.q1.placeholder'
              },
              {
                id: 'confidence',
                questionKey: 'assessments.completion.q2.question',
                type: 'scale',
                scaleMin: 1,
                scaleMax: 5,
                scaleLabels: [
                  'assessments.completion.q2.labelLow',
                  'assessments.completion.q2.labelHigh'
                ]
              },
              {
                id: 'nextStep',
                questionKey: 'assessments.completion.q3.question',
                type: 'single-choice',
                options: [
                  { id: 'a', labelKey: 'assessments.completion.q3.optionA' },
                  { id: 'b', labelKey: 'assessments.completion.q3.optionB' },
                  { id: 'c', labelKey: 'assessments.completion.q3.optionC' },
                  { id: 'd', labelKey: 'assessments.completion.q3.optionD' },
                  {
                    id: 'e',
                    labelKey: 'assessments.completion.q3.optionE',
                    other: true,
                    otherPlaceholderKey: 'assessments.completion.q3.optionEPlaceholder'
                  }
                ]
              }
            ],
            feedbackKey: 'assessments.completion.feedback'
          }
        }
      ]
    }
  ]
};

// Helper Functions
function getTotalSteps() {
  return COURSE_DATA.modules.reduce((acc, mod) => acc + mod.steps.length, 0);
}

function getStepById(stepId) {
  for (const mod of COURSE_DATA.modules) {
    const step = mod.steps.find((s) => s.id === stepId);
    if (step) {
      return { module: mod, step };
    }
  }
  return null;
}

function getNextStep(currentModuleIndex, currentStepIndex) {
  const currentModule = COURSE_DATA.modules[currentModuleIndex];
  if (currentStepIndex < currentModule.steps.length - 1) {
    return { moduleIndex: currentModuleIndex, stepIndex: currentStepIndex + 1 };
  } else if (currentModuleIndex < COURSE_DATA.modules.length - 1) {
    return { moduleIndex: currentModuleIndex + 1, stepIndex: 0 };
  }
  return null;
}

function getPreviousStep(currentModuleIndex, currentStepIndex) {
  if (currentStepIndex > 0) {
    return { moduleIndex: currentModuleIndex, stepIndex: currentStepIndex - 1 };
  } else if (currentModuleIndex > 0) {
    const prevModule = COURSE_DATA.modules[currentModuleIndex - 1];
    return { moduleIndex: currentModuleIndex - 1, stepIndex: prevModule.steps.length - 1 };
  }
  return null;
}

function getModuleProgress(moduleIndex, completedSteps) {
  const module = COURSE_DATA.modules[moduleIndex];
  const completed = module.steps.filter((s) => completedSteps.includes(s.id)).length;
  return Math.round((completed / module.steps.length) * 100);
}

function getJourneyProgress(completedSteps) {
  const totalSteps = getTotalSteps();
  return Math.round((completedSteps.length / totalSteps) * 100);
}

// Export to window for global access
window.COURSE_DATA = COURSE_DATA;
window.getTotalSteps = getTotalSteps;
window.getStepById = getStepById;
window.getNextStep = getNextStep;
window.getPreviousStep = getPreviousStep;
window.getModuleProgress = getModuleProgress;
window.getJourneyProgress = getJourneyProgress;

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    COURSE_DATA,
    getTotalSteps,
    getStepById,
    getNextStep,
    getPreviousStep,
    getModuleProgress,
    getJourneyProgress
  };
}
